import React, { useContext, useState, useEffect, useRef } from 'react';
import DataSourcesTable from './DataSourcesTable';
import { Row, Col, Dropdown } from 'react-bootstrap';
import { FaConnectdevelop } from 'react-icons/fa';
import { BsUpload, BsDownload } from 'react-icons/bs';
import AppContext from 'Utils/AppContext';
import './DataSource.css';
import FetchContainer from 'Utils/FetchContainer';
import PageHeader from 'Utils/PageHeader';
import {
  createDataSource,
  toRequestDto,
  parseDataSourcesImportJson,
  normalizeImportRow,
  connectionsForExportJson,
} from './datasourcesApi';
import { getErrorMessage } from 'Utils';

const DataSources = (props) => {
  const [fetchingData, setFetchingData] = useState(false);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);
  const {
    loadConnectionsHandler,
    fetchNewEntitlements,
    setNotification,
    isAdmin,
    dataSourceImportExportEnabled,
  } = useContext(AppContext);
  // Import/Export are hidden unless an admin flipped the advanced-config gate AND the current user
  // is an admin (C2D-1000/998). Double-gated: a mis-formed import can corrupt the source list.
  const showImportExport = isAdmin === true && dataSourceImportExportEnabled === true;

  useEffect(() => {
    const init = async () => {
      setFetchingData(true);
      await loadConnectionsHandler();
      setFetchingData(false);
    }
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount
  }, []);

  useEffect(() => {
    // console.log('[Data sources listing]', JSON.stringify(props.connections, null, 2));
  }, [props.connections]);

  const runImport = async (file) => {
    setImporting(true);
    try {
      const text = await file.text();
      let parsed;
      try {
        parsed = JSON.parse(text);
      } catch (e) {
        setNotification({
          header: 'Import failed',
          body: <span>The file is not valid JSON.</span>,
        });
        return;
      }
      const rawList = parseDataSourcesImportJson(parsed);
      const existing = new Set(
        (props.connections || []).map((c) => String(c.name || '').toLowerCase())
      );
      const normalized = [];
      const seenInFile = new Set();
      for (const raw of rawList) {
        const row = normalizeImportRow(raw);
        if (!row) continue;
        const key = row.name.toLowerCase();
        if (seenInFile.has(key)) continue;
        seenInFile.add(key);
        if (existing.has(key)) continue;
        normalized.push(row);
      }
      if (!normalized.length) {
        setNotification({
          header: 'Nothing to import',
          body: (
            <span>
              No new data sources to add (missing names, duplicates, or every name already exists).
            </span>
          ),
        });
        return;
      }

      let created = 0;
      const failures = [];
      for (const row of normalized) {
        try {
          await createDataSource(toRequestDto(row));
          created++;
          existing.add(row.name.toLowerCase());
        } catch (e) {
          failures.push(`${row.name}: ${getErrorMessage(e)}`);
        }
      }

      await loadConnectionsHandler();
      if (fetchNewEntitlements) await fetchNewEntitlements();

      const parts = [`Imported ${created} data source${created === 1 ? '' : 's'}.`];
      if (failures.length) {
        parts.push(` Failed: ${failures.slice(0, 5).join('; ')}`);
        if (failures.length > 5) parts.push(` …and ${failures.length - 5} more.`);
      }
      setNotification({
        header: 'Import finished',
        body: <span>{parts.join('')}</span>,
      });
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const onFileSelected = (e) => {
    const f = e.target.files && e.target.files[0];
    if (f) runImport(f);
  };

  // Export the data sources as import-ready JSON. connectionsForExportJson strips every secret
  // field, so the download carries source definitions only — credentials are re-entered on the
  // target install (C2D-1000/998).
  const runExport = () => {
    const rows = connectionsForExportJson(props.connections || []);
    if (!rows.length) {
      setNotification({ header: 'Nothing to export', body: <span>There are no data sources to export.</span> });
      return;
    }
    const blob = new Blob([JSON.stringify({ dataSources: rows }, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `c2-data-sources-${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setNotification({
      header: 'Export ready',
      body: <span>Exported {rows.length} data source{rows.length === 1 ? '' : 's'} (credentials excluded).</span>,
    });
  };

  const busy = fetchingData || props.fetchingData || importing;

  return (
    <div className='p-0 h-100'>
      <input
        ref={fileInputRef}
        type='file'
        accept='application/json,.json'
        className='d-none'
        aria-hidden='true'
        onChange={onFileSelected}
      />
      <FetchContainer fetchingData={busy} absolute={true}>
        <div>
          <PageHeader
            header='Data sources'
            icon={FaConnectdevelop}
            tooltip='Create or manage data sources.'
            actions={showImportExport ? [
              <Dropdown.Item
                key='import'
                onClick={() => fileInputRef.current && fileInputRef.current.click()}
                disabled={importing}
              >
                <BsUpload className='mr-2' />
                Import Data Sources
              </Dropdown.Item>,
              <Dropdown.Item
                key='export'
                onClick={runExport}
                disabled={busy}
              >
                <BsDownload className='mr-2' />
                Export Data Sources
              </Dropdown.Item>,
            ] : []}
          />
          <Row>
            <Col>
              <DataSourcesTable
                connections={props.connections}
                setConnection={(conn) => props.setConnection(conn)}
                fetchingData={props.fetchingData}
              />
            </Col>
          </Row>
        </div>
      </FetchContainer>
    </div>
  );
}

export default DataSources;
